import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Baby, ArrowLeft, Mail, X } from 'lucide-react';
import { useGame } from '../context/GameContext';

const ChildSetup = () => {
  const navigate = useNavigate();
  const { setPlayerName } = useGame();
  const [childName, setChildName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [error, setError] = useState('');
  const [showReturningModal, setShowReturningModal] = useState(false);
  const [returningEmail, setReturningEmail] = useState('');
  const [returningError, setReturningError] = useState('');

  const handleSubmit = () => {
    if (!childName.trim()) {
      setError('Please enter your name!');
      return;
    }
    
    if (!parentEmail.trim() || !parentEmail.includes('@')) {
      setError('Please enter a valid parent email address!');
      return;
    }

    setPlayerName(childName);
    localStorage.setItem('userRole', 'child');
    localStorage.setItem('pendingParentEmail', parentEmail.trim());
    
    navigate('/payment');
  };

  const handleReturningPlayer = () => {
    if (!returningEmail.trim() || !returningEmail.includes('@')) {
      setReturningError('Please enter a valid parent email!');
      return;
    }
    
    // Store the parent email filter and navigate to profile selection
    localStorage.setItem('parentEmailFilter', returningEmail.trim().toLowerCase());
    navigate('/profile-selection');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
    }}>
      <div className="max-w-2xl w-full">
        <Button
          onClick={() => navigate('/role-selection')}
          variant="ghost"
          className="mb-6 text-lg font-semibold text-white hover:scale-110 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <Card className="p-8 md:p-12 bg-white/95 backdrop-blur-sm shadow-2xl bounce-in" style={{
          borderRadius: '32px'
        }}>
          <div className="text-center mb-8">
            <Baby className="w-16 h-16 mx-auto mb-4 text-pink-500" />
            <h2 className="text-3xl font-bold text-gray-800 mb-2">What's Your Name?</h2>
            <p className="text-gray-600 text-lg">Let's start your learning adventure!</p>
          </div>

          <div className="space-y-4">
            <Input
              type="text"
              placeholder="Enter your name..."
              value={childName}
              onChange={(e) => { setChildName(e.target.value); setError(''); }}
              className="text-xl p-6 text-center border-4 border-pink-300 focus:border-pink-500"
              style={{ borderRadius: '16px' }}
            />

            {/* Parent Email - Required */}
            <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
              <p className="text-sm font-semibold text-blue-700 text-center mb-2">
                👨‍👩‍👧 Parent's Email <span className="text-red-500">*</span>
              </p>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  placeholder="parent@email.com"
                  value={parentEmail}
                  onChange={(e) => { setParentEmail(e.target.value); setError(''); }}
                  className="text-lg p-4 pl-12 text-center border-2 border-blue-300 focus:border-blue-500"
                  style={{ borderRadius: '12px' }}
                />
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-center font-semibold">{error}</p>
            )}

            <Button
              onClick={handleSubmit}
              disabled={!childName.trim() || !parentEmail.trim()}
              className="w-full py-6 text-xl font-bold transition-all duration-300 hover:scale-105 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                borderRadius: '16px'
              }}
            >
              Start Learning!
            </Button>

            {/* Returning Player Link */}
            <div className="text-center pt-4">
              <p className="text-gray-500 text-sm">
                🎮 Returning Player?{' '}
                <button
                  onClick={() => setShowReturningModal(true)}
                  className="text-purple-600 font-semibold hover:underline"
                >
                  Click here
                </button>
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Returning Player Modal */}
      {showReturningModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6 bg-white" style={{ borderRadius: '24px' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Welcome Back! 🎮</h3>
              <button
                onClick={() => {
                  setShowReturningModal(false);
                  setReturningEmail('');
                  setReturningError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              Enter your parent's email to find your profile:
            </p>

            <div className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  placeholder="parent@email.com"
                  value={returningEmail}
                  onChange={(e) => { setReturningEmail(e.target.value); setReturningError(''); }}
                  className="text-lg p-4 pl-12 text-center border-2 border-purple-300 focus:border-purple-500"
                  style={{ borderRadius: '12px' }}
                />
              </div>

              {returningError && (
                <p className="text-red-500 text-center font-semibold text-sm">{returningError}</p>
              )}

              <Button
                onClick={handleReturningPlayer}
                disabled={!returningEmail.trim()}
                className="w-full py-4 text-lg font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white disabled:opacity-50"
                style={{ borderRadius: '12px' }}
              >
                Find My Profile
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ChildSetup;
